// This page handles the OAuth callback from Google
// Updated to use project's authentication context instead of deprecated @supabase/auth-helpers-react
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { supabase } from '@/integrations/supabase/client'

export default function GoogleCallback() {
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  
  useEffect(() => {
    const handleCallback = async () => {
      // Get the code and state from the URL
      const searchParams = new URLSearchParams(location.search)
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      
      // Check if there's an error
      if (error) {
        setStatus('error')
        setErrorMessage('Authentication was denied or canceled.')
        return
      }
      
      // Check if code and state are present
      if (!code || !state) {
        setStatus('loading')
        return
      }
      
      // Verify the state parameter
      const storedState = localStorage.getItem('googleOAuthState')
      if (state !== storedState) {
        setStatus('error')
        setErrorMessage('Security verification failed. Please try again.')
        return
      }
      
      // Clear the state from localStorage
      localStorage.removeItem('googleOAuthState')
      
      try {
        // Calculate the redirect URI
        const redirectUri = `${window.location.origin}/integrations/google-callback`
        
        // Exchange the code for tokens
        const { data, error } = await supabase.functions.invoke('connect-google-drive', {
          method: 'POST',
          body: { code, redirectUri },
        })
        
        if (error) {
          throw error
        }
        
        // Success
        setStatus('success')
        toast({
          title: 'Google Drive Connected',
          description: `Successfully connected to Google Drive with account ${data.google_email}.`,
        })
      } catch (error) {
        setStatus('error')
        setErrorMessage('Failed to connect Google Drive. Please try again.')
      }
    }
    
    handleCallback()
  }, [location.search, toast])
  
  const goToIntegrations = () => {
    navigate('/integrations')
  }
  
  return (
    <div className="container flex items-center justify-center min-h-screen py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Google Drive Integration</CardTitle>
          <CardDescription>
            {status === 'loading' && 'Connecting your Google Drive account...'}
            {status === 'success' && 'Your Google Drive account has been connected!'}
            {status === 'error' && 'There was a problem connecting your account.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'loading' && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          
          {status === 'success' && (
            <div className="py-4 text-center text-muted-foreground">
              You can now import documents from Google Drive into Posit.
            </div>
          )}
          
          {status === 'error' && (
            <div className="py-4 text-center text-destructive">
              {errorMessage || 'An unexpected error occurred. Please try again.'}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          {(status === 'success' || status === 'error') && (
            <Button onClick={goToIntegrations}>
              Go to Integrations
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
} 